import req from '../../api/request'
import type { LandingNotification } from '../../features/notifications/types/landing'

interface NotificationEnvelope {
  data?: LandingNotification
}

// A notification is identified by root-level `id` + `type`. Its own payload
// lives under `data` — so the presence of `data` alone cannot distinguish a
// notification from an envelope wrapping one.
const isNotification = (value: unknown): value is LandingNotification => {
  if (!value || typeof value !== 'object') {
    return false
  }
  const candidate = value as Partial<LandingNotification>
  return typeof candidate.id === 'string' && typeof candidate.type === 'string'
}

/**
 * GET /notifications/:id — fetches a single notification for the web detail
 * page. This function is the ONLY place that knows the response envelope.
 *
 * The live endpoint returns the notification at the ROOT (verified against
 * /v1/notifications/:id), and every notification carries its own `data` object
 * of payload fields. So the unwrap must NOT branch on "is there a `data` key" —
 * that would mistake a notification's payload for an envelope and strip its
 * `id`/`type`, which in turn loses the registry action lookup. Instead, prefer
 * the root object whenever it looks like a notification, and only unwrap when
 * it doesn't.
 */
export const getNotification = async (
  notificationId: string
): Promise<LandingNotification> => {
  const response = (await req(`/notifications/${notificationId}`)) as
    | NotificationEnvelope
    | LandingNotification

  if (isNotification(response)) {
    return response
  }

  const envelope = response as NotificationEnvelope

  if (
    envelope &&
    typeof envelope === 'object' &&
    isNotification(envelope.data)
  ) {
    return envelope.data
  }

  return response as LandingNotification
}
